import React, { useState, useEffect } from 'react';
import { fetchFraudAlerts } from '../services/api';
import { toTitleCase } from '../utils/format';

const PAGE_SIZE = 50;

function todayET() {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function FraudAlert({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const load = (p) => {
    setLoading(true);
    setError(null);
    fetchFraudAlerts(p, PAGE_SIZE)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(page); }, [page]);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;
  const today = todayET();

  const scoreColor = (score) => {
    if (score >= 75) return '#c0392b';
    if (score >= 50) return '#b06a00';
    return '#1a8a4a';
  };

  const fmtShipDate = (d) => {
    if (!d) return '—';
    // shipdate may be YYYY-MM-DD or a string; parse as local date to avoid UTC shift
    const parts = String(d).slice(0, 10).split('-');
    if (parts.length === 3) {
      const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return d;
  };

  const isToday = (d) => {
    if (!d) return false;
    return String(d).slice(0, 10) === today;
  };

  const sameDayCount = rows.filter(r => isToday(r.shipDate)).length;
  // The live queue has no dates on it; CPx's own copy has no ship-to. Show what there is.
  const live = !!data?.live;
  const placeLine = (p) => {
    if (!p) return null;
    const [city, st] = p.split(', ');
    return <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{toTitleCase(city)}{st ? `, ${st}` : ''}</div>;
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Fraud Alerts</h2>
        {sameDayCount > 0 && (
          <span style={{ background: '#c0392b', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4 }}>
            {sameDayCount} shipping today
          </span>
        )}
      </div>

      <div style={{ fontSize: 12, color: 'var(--gray-500)', margin: '-8px 0 12px' }}>
        {live
          ? 'Orders the fraud check flagged that nobody has marked as fraud or put on hold yet, newest first. Click an order to open it.'
          : 'Orders with a fraud score, newest first. Click an order to open it.'}
      </div>

      <div className="card">
        {data && !live && !loading && (
          <div style={{ padding: '8px 16px', fontSize: 12, color: '#7a5b00', background: '#fffaf0', borderBottom: '1px solid #f0c36d' }}>
            The live fraud queue could not be reached, so this is every order with a fraud score, not only the ones waiting on a decision.
          </div>
        )}
        <div className="card-header">Flagged Orders {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No fraud alerts.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Email</th>
                    <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Fraud Score</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Total</th>
                    {live && <th style={{ textAlign: 'left', padding: '8px 12px 8px 20px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship To</th>}
                    {!live && <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Order Date</th>}
                    {!live && <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Date</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>
                        {toTitleCase(row.billTo ?? row.customerName ?? '—')}
                        {placeLine(row.billPlace)}
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)', fontSize: 11 }}>{row.email ?? row.customerEmail ?? '—'}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor(row.fraudScore ?? 0) }}>
                          {row.fraudScore ?? '—'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>${Number(row.orderTotal ?? 0).toFixed(2)}</td>
                      {live && (
                        <td style={{ padding: '8px 12px 8px 20px', color: 'var(--navy)' }}>
                          {toTitleCase(row.shipTo || '—')}
                          {placeLine(row.shipPlace)}
                        </td>
                      )}
                      {!live && <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {row.orderDate ? new Date(row.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>}
                      {!live && <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {isToday(row.shipDate) ? (
                          <span style={{ background: '#c0392b', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3, marginRight: 4 }}>TODAY</span>
                        ) : null}
                        <span style={{ color: isToday(row.shipDate) ? '#c0392b' : 'var(--gray-500)', fontWeight: isToday(row.shipDate) ? 700 : 400 }}>
                          {fmtShipDate(row.shipDate)}
                        </span>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={live ? page * PAGE_SIZE >= total : rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
